import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Dropzone, { ExtendedFile } from "../../common/Dropzone";
import { useForm } from "react-hook-form";
import { FileUploadOutlined } from "@mui/icons-material";
import CustomTextField from "../../common/CustomTextField";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { FileUp } from "lucide-react";
import { useModalStyles } from "../../common/useModalStyles";
import CustomDatePicker from "../../common/CustomDatePicker";
import { convertBase64, FieldLabel, sortNames } from "../../../utils/Utility";
import { fetchManufacturersListUserSlice, useUserReducer } from "../../../redux/slice/userSlice";
import { ManufacturerInfo, setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { fetchProductDocumentsByProductId, ProductDocument, updateElementInProductSlice, updateProductObjectValues, uploadProductDocument, useProductReducer } from "../../../redux/slice/product/createProductSlice";
import dayjs from "dayjs";
import { CodeTable, fetchAllCodeTablesValues, getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { pdf, doc, docx, jpeg, png, xls, xlsx, mov, mp4, avi, amv, asf, dv, f4v, flv, m4v, mkv, mxf, ogv, rm, rmvb, ts, video3gp, vob, webm, wmv, video3g2, PRODUCT_DOCUMENT_TYPES, PRODUCT_DOCUMENT_STATUS } from "../../../constants/applicationConstants";

interface ProductDocumentPopupProps {
  open: boolean;
  onClose: () => void;
}

interface Attachment {
  documentName: string;
  content: string;
  fileType: string;
}

type Option = {
  label: string;
  value: string | number;
}


const ProductDocumentPopup: React.FC<ProductDocumentPopupProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    inputFieldSx,
    dropdownWrapSx,
    datepickerWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const [activeTab, setActiveTab] = useState(0);
  const { manufacturerInfoListUserSlice } = useUserReducer();
  const { uploadProductDocumentStatus } = useProductReducer();
  const dispatch = useAppDispatch();
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();
  const [productDocumentList, setProductDocumentList] = React.useState<CodeTable[]>([]);
  

  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
    setValue('manufacturer.manufacturerId', null);
  };

  const { control, handleSubmit, setValue, getValues, formState: { errors } } = useForm<ProductDocument>({
    defaultValues: {
      attachmentProductDocument: [],
    },
  });

  const statusUpdateDocumentTypes = [
    PRODUCT_DOCUMENT_TYPES.IVR_FORM,
     PRODUCT_DOCUMENT_TYPES.ORDER_FORM, 
     PRODUCT_DOCUMENT_TYPES.SERVICE_AGREEMENT, 
     PRODUCT_DOCUMENT_TYPES.TISSUE_TRACKING_FORM, 
     PRODUCT_DOCUMENT_TYPES.RETURN_FORM
    ];

  const onSubmit = async(data: ProductDocument) => {
    data.externalLinkType = "Product Document";
    data.productId = data.manufactureId ? null : data.productId;
    if(statusUpdateDocumentTypes.includes(data?.documentType)) {
      data.status = PRODUCT_DOCUMENT_STATUS.PENDING;
    }
    const response = await dispatch(uploadProductDocument({productDocument: data}));
  };

  const productDocumentOptions = useMemo(() => {
    return productDocumentList.map(item => ({
      label: item.displayValue,
      value: item.keyName
    }));
  }, [productDocumentList]);

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sortedDocumentList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "ProductDocument"),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setProductDocumentList(sortedDocumentList);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);


  useEffect(() => {
    if(uploadProductDocumentStatus === 'success') {
      let successObj: SingleAlertInfo = {
        message: "Product document(s) saved successfully.",
        alertType: "success",
      };
      dispatch(setSingleAlertObj(successObj));
      dispatch(updateElementInProductSlice({
        elementName : "uploadProductDocumentStatus",
        value: 'idle'
      }));
      dispatch(fetchProductDocumentsByProductId(getValues('productId') || 0));
      onClose();
    }
  }, [uploadProductDocumentStatus]);

  //mount
  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    let id = urlSearchParams.get('id');
    if(id) {
      setValue('productId', parseInt(id));
    };
    // Set effectiveDate to today's date
    const today = dayjs().format("MM/DD/YYYY");
    setValue('effectiveDate', today);
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    const responseManufacturers = await dispatch(fetchManufacturersListUserSlice());
    const responseAllCodeTableValues = await dispatch(fetchAllCodeTablesValues());
  }

  const handleEffectiveDate = (date: Date | null) => {
    if (dayjs(date).isValid()) {
        setValue("effectiveDate", dayjs(date).format("MM/DD/YYYY"));
    };
};


  const handleDropzoneChange = async (uploadedFiles: ExtendedFile[]) => {
    const readFileAsBase64 = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
    };
    const attachments: Attachment[] = await Promise.all(
      uploadedFiles.map(async (file) => {
        const base64Content = await readFileAsBase64(file);
        return {
          documentName: file.name,
          content: base64Content.split(",")[1],
          fileType: file.type,
        };
      })
    );
    const documentName = attachments[0]?.documentName || "";
    const name = documentName?.substring(0, documentName.lastIndexOf('.')) || documentName; 
    setValue('name', name);
    setValue("attachmentProductDocument", attachments);
  };

  const manufacturersList = useMemo(() => {
    let options: Option[] = [];
    if (manufacturerInfoListUserSlice && manufacturerInfoListUserSlice.length > 0) {
      const sortedInfo: ManufacturerInfo[] = sortNames(manufacturerInfoListUserSlice, 'manufacturerName');
      sortedInfo?.map(option => {
        options.push({ label: option.manufacturerName, value: option.manufacturerId });
      });
    }
    return options;
  }, [manufacturerInfoListUserSlice]);

  const title = "Upload Document";
  const subtitle =
    "Product Document - Upload your document here. Supported formats: PDF, DOC, DOCX, JPEG, PNG, XLS, XLSX, MP4, MOV. Maximum file size: 15MB.";
  const ariaDescId = "product-document-dialog-description";
  const formErrors = errors as Record<string, { message?: string } | undefined>;

  return (
    <>
      <Dialog
        open={open}
        keepMounted
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: {
            ...paperSx(isMobile),
            maxHeight: isMobile ? "100dvh" : "calc(100dvh - 32px)",
          },
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
        aria-describedby={ariaDescId}
      >
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box sx={headerSx}>
            <Box sx={iconBoxSx(tokens.accent)}>
              <FileUp size={20} color={tokens.accent} strokeWidth={1.8} />
            </Box>
            <Box sx={{ flex: 1, pr: 4 }}>
              <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                {title}
              </Typography>
              <Typography id={ariaDescId} sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                {subtitle}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
              <CloseIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              px: 2.8,
              pt: 2.4,
              pb: 1,
              borderBottom: `1px solid ${tokens.border}`,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                justifyContent: "center",
                border: `1px solid ${tokens.border}`,
                borderRadius: "12px",
                backgroundColor: tokens.inputBg,
                padding: "4px",
                overflow: "hidden",
              }}
            >
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px" }}>
                <ButtonBase
                  onClick={() => handleTabChange(0)}
                  sx={{
                    height: 36,
                    px: 1.5,
                    borderRadius: "10px",
                    fontWeight: "bold",
                    fontSize: "0.92rem",
                    lineHeight: 1.2,
                    color: activeTab === 0 ? "#062b28" : tokens.textPrimary,
                    backgroundColor: activeTab === 0 ? tokens.accent : "transparent",
                    transition: "background-color 0.25s ease, color 0.25s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  Product Wise Document
                </ButtonBase>
                <ButtonBase
                  onClick={() => handleTabChange(1)}
                  sx={{
                    height: 36,
                    px: 1.5,
                    borderRadius: "10px",
                    fontWeight: "bold",
                    fontSize: "0.92rem",
                    lineHeight: 1.2,
                    color: activeTab === 1 ? "#062b28" : tokens.textPrimary,
                    backgroundColor: activeTab === 1 ? tokens.accent : "transparent",
                    transition: "background-color 0.25s ease, color 0.25s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  Manufacturer Wise Document
                </ButtonBase>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              ...bodySx,
              pt: 2.2,
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarGutter: "stable",
              minHeight: 0,
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                borderRadius: 4,
              },
            }}
          >

            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <Box>
                  <FieldLabel label="Document Type" required tokens={tokens} />
                  <Box sx={dropdownWrapSx(!!errors.documentType)}>
                    <CustomDropdownField
                      name="documentType"
                      control={control}
                      options={productDocumentOptions}
                      size = "small"
                      label=""
                      rules={{ required: "Document type is required." }}
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item md={6} xs={12}>
                <Box>
                  <FieldLabel label="Effective Date" required tokens={tokens} />
                  <Box sx={datepickerWrapSx(!!errors.effectiveDate)}>
                    <CustomDatePicker
                      name="effectiveDate"
                      controllerName="effectiveDate"
                      control={control}
                      size = "small"
                      label=""
                      onChange={handleEffectiveDate}
                      rules={{ required: "Effective Date is required." }}
                    />
                  </Box>
                </Box>
              </Grid>
              { activeTab === 1 && <Grid item md={6} xs={12}>
                  <Box>
                    <FieldLabel label="Manufacturer" required tokens={tokens} />
                    <Box sx={dropdownWrapSx(!!errors.manufactureId)}>
                      <CustomDropdownField
                        name="manufactureId"
                        control={control}
                        options={manufacturersList}
                        size = "small"
                        label=""
                        rules={{ required: "Manufacturer is required." }}
                      />
                    </Box>
                  </Box>
                </Grid>
              }
              <Grid item xs={12}>
                <Dropzone
                  name="attachmentProductDocument"
                  label=""
                  onFileChange={() => { }}
                  onFileChangeTest={handleDropzoneChange}
                  control={control}
                  maxFileCount={1}
                  rules={{ required: "Product document is required." }}
                  maxFileSize={{ size: 15, unit: "MB" }}
                  allowedFileTypes={{
                    ...pdf,
                    ...doc,
                    ...docx,
                    ...jpeg,
                    ...png,
                    ...xls,
                    ...xlsx,
                    ...mov,
                    ...mp4,
                    ...avi,
                    ...flv,
                    ...ogv,
                    ...webm,
                    ...ts,
                    ...mkv,
                    ...wmv,
                    ...video3gp,
                    ...video3g2,
                    ...m4v,
                    ...asf,
                    ...f4v,
                    ...rm,
                    ...rmvb,
                    ...mxf,
                    ...vob,
                    ...dv,
                    ...amv,
                  }}
                />
                <Box display="flex" justifyContent="space-between" mt={1} flexWrap="wrap" gap={1}>
                  <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary }} component="p">
                    Support Format : PDF, DOC, DOCX, JPEG, PNG, XLS, XLSX, MP4, MOV.
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary }} component="p">
                    Maximum size : 15MB
                  </Typography>
                </Box>
              </Grid>
              <Grid item md={12} xs={12} textAlign="center">
                <Typography sx={{ fontSize: "1.25rem", fontWeight: 600, color: tokens.textPrimary }}>OR</Typography>
              </Grid>
              <Grid item md={12}>
                <Box>
                  <FieldLabel label="Document URL" tokens={tokens} />
                  <Box sx={inputFieldSx(!!(errors as { productDocumentInfo?: { documentURL?: { message?: string } } })?.productDocumentInfo?.documentURL)}>
                    <CustomTextField
                      controllerName="productDocumentInfo.documentURL"
                      control={control}
                      size = "small"
                      label=""
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <FieldLabel label="Name" required tokens={tokens} />
                  <Box sx={inputFieldSx(!!errors.name)}>
                    <CustomTextField
                      control={control}
                      controllerName="name"
                      size = "small"
                      label=""
                      rules={{ required: "Name is required." }}
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <FieldLabel label="Comments" tokens={tokens} />
                  <Box sx={inputFieldSx(!!formErrors.comments)}>
                    <CustomTextField
                      control={control}
                      controllerName="comments"
                      label=""
                      multiline
                      rows={3}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box sx={footerSx}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box
                component="button"
                type="submit"
                sx={{
                  ...primaryBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "none",
                  cursor: "pointer",
                  color: "#fff",
                }}
              >
                <FileUploadOutlined sx={{ fontSize: "1.1rem" }} />
                Upload
              </Box>
              <Box
                component="button"
                type="button"
                onClick={onClose}
                sx={{
                  ...cancelBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "1px solid",
                  cursor: "pointer",
                }}
              >
                Cancel
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default ProductDocumentPopup;
